import React from "react";
import { VideoPlayerContainer } from "mediaModules/media/components/VideoPlayerContainer";
import { MediaAsset, TranscodeStatus } from "mediaModules/media/models";
import { THUMBNAIL_VIDEO_VIEWER_POPUP_PROPS } from "mediaModules/media/pages/EditVideoPage/components/ThumbnailVideoViewer";

import { IExternalMedia } from "shared-types";
import { externalMediaMapToVideoModel } from "shared-services";
import { ShowreelThumb } from "mediaModules/media/pages/VideoPage/components/ShowreelThumb";
import { heapTrackingForShowreels } from "app/helpers/heapTracking";
import "./Showreels.scss";

export interface IShowreelsProps {
  showreels: MediaAsset[];
  openPopup: (popupContent, popupProps) => void;
  closePopup: () => void;
  mediaObjectPromiseMap: Map<string, Promise<MediaAsset>>;
  titlePrefix: string;
  updateProcessedShowreelsCount: (count: number) => void;
  externalShowreels?: IExternalMedia[];
}

const mapToVideoModel = (mediaAsset: MediaAsset) => {
  const {
    id,
    durationInMs,
    sources,
    title,
    thumbnails,
    selectedThumbnail,
    downloadLink,
  } = mediaAsset;

  return {
    id,
    sources,
    duration: durationInMs,
    fileName: title,
    previewImg: thumbnails[selectedThumbnail],
    poster: thumbnails[0],
    downloadLink,
    shareLink: downloadLink,
  };
};

export const Showreels: React.FC<React.PropsWithChildren<IShowreelsProps>> = (
  props
) => {
  const [showreels, setShowreels] = React.useState<MediaAsset[]>(
    props.showreels
  );

  const updateShowreelsByMediaObjects = async () => {
    const updatedShowreelsPromises = showreels.map(
      (showreel) => props.mediaObjectPromiseMap.get(showreel.mediaInfoId)!
    );
    const updatedShowreels = await Promise.all(updatedShowreelsPromises);
    const withCompleteStatus = updatedShowreels.filter(
      ({ transcodeStatus }) => transcodeStatus === TranscodeStatus.Complete
    );
    setShowreels(withCompleteStatus);
    props.updateProcessedShowreelsCount(withCompleteStatus.length);
  };

  React.useEffect(() => {
    if (!(props.externalShowreels && props.externalShowreels.length > 0)) {
      updateShowreelsByMediaObjects();
    }
  }, []);

  const openVideoPlayer = ({ id }: MediaAsset) => {
    heapTrackingForShowreels();

    const videoList = showreels.map((mediaAsset) =>
      mapToVideoModel(mediaAsset)
    );
    const selectedVideo = videoList.find((item) => item.id === id)!;

    const videoPlayer = (
      <VideoPlayerContainer
        playerConfig={{
          videoList,
          selectedVideo,
          hasCloseButton: true,
          autoPlay: true,
          selectVideo: () => undefined,
          close: props.closePopup,
          titlePrefix: props.titlePrefix,
        }}
      />
    );

    props.openPopup(videoPlayer, THUMBNAIL_VIDEO_VIEWER_POPUP_PROPS);
  };

  const renderShowreel = (showreel: MediaAsset) => {
    return (
      <div key={showreel.id} className="c-media-section-showreels__showreel">
        <ShowreelThumb
          showreel={showreel}
          playbackVideo={openVideoPlayer}
          isViewOnly
        />
      </div>
    );
  };

  const openVideoPlayerExternalMedia = ({ id }: IExternalMedia) => {
    const videoList =
      props.externalShowreels?.map(externalMediaMapToVideoModel) || [];
    const selectedVideo = videoList.find((item) => item.id === id)!;

    const videoPlayer = (
      <VideoPlayerContainer
        playerConfig={{
          videoList,
          selectedVideo,
          hasCloseButton: true,
          autoPlay: true,
          selectVideo: () => undefined,
          close: props.closePopup,
          titlePrefix: props.titlePrefix,
        }}
      />
    );

    props.openPopup(videoPlayer, THUMBNAIL_VIDEO_VIEWER_POPUP_PROPS);
  };

  const renderExternalShowreel = (externalShowreel: IExternalMedia) => {
    // Makes an external asset act like a MediaAsset (internal asset)
    const modifiedShowreel = {
      ...externalShowreel,
      thumbnails: [externalShowreel.thumbnail],
      selectedThumbnail: 0,
      visible: true,
    };

    return (
      <div
        key={externalShowreel.id}
        className="c-media-section-showreels__showreel"
      >
        {/* @ts-expect-error - check types */}
        <ShowreelThumb
          showreel={modifiedShowreel}
          playbackVideo={openVideoPlayerExternalMedia}
          isViewOnly
        />
      </div>
    );
  };

  return (
    <div className="c-media-section-showreels">
      {showreels.map(renderShowreel)}
      {props?.externalShowreels?.map(renderExternalShowreel)}
    </div>
  );
};
